/**
 * Declarative page registry for the mobile shell. The same data drives bottom navigation, page
 * titles, and breadcrumbs (Atlas: Navigation Menu with Data-Driven Page Mapping). New feature
 * routes register here once and pick up nav + breadcrumbs automatically.
 *
 * Path keys are end-anchored regexes so breadcrumb resolution can match nested segments. Keep the
 * destination list short — bottom navigation is for a handful of module-level destinations only.
 */

export const ROOT_PATH = "/mobile";

export const pageMappings: PageMappingTable = {
	"/mobile": { icon: "home", label: "Inventory", nav: false, title: "Inventory" },
	"/mobile/finish": { icon: "spa", label: "Finish", nav: true, title: "Finished Goods" },
	"/mobile/material": { icon: "inventory_2", label: "Material", nav: true, title: "Raw Material" },
	"/mobile/retail": { icon: "storefront", label: "Retail", nav: true, title: "Retail" },
	"/mobile/history": { icon: "history", label: "Activity", nav: true, title: "Activity & Sync" }
};

/** Bottom-navigation destinations, in display order. */
export const navDestinations = Object.entries(pageMappings)
	.filter(([, mapping]) => mapping.nav)
	.map(([path, mapping]) => ({ icon: mapping.icon, label: mapping.label, path }));

/**
 * Resolves breadcrumb trail for a pathname by accumulating each segment and matching the registry.
 * Keys are treated as end-anchored regexes so detail segments can be matched by later slices.
 */
export const getBreadcrumbs = (pathname: string): Array<{ label: string; link: string }> => {
	const segments = pathname.split("/").filter(Boolean);
	const crumbs: Array<{ label: string; link: string }> = [];
	let partPath = "";
	for (const segment of segments) {
		partPath += `/${segment}`;
		const matchKey = Object.keys(pageMappings).find((key) => new RegExp(`${key}$`).test(partPath));
		if (matchKey) {
			crumbs.push({ label: pageMappings[matchKey].label, link: partPath });
		}
	}
	return crumbs;
};

/** Resolves the document/page title for a pathname (falls back to the root title). */
export const getPageTitle = (pathname: string): string => {
	const segments = pathname.split("/").filter(Boolean);
	let partPath = "";
	let title = pageMappings[ROOT_PATH]?.title ?? "Inventory";
	for (const segment of segments) {
		partPath += `/${segment}`;
		const matchKey = Object.keys(pageMappings).find((key) => new RegExp(`${key}$`).test(partPath));
		if (matchKey) title = pageMappings[matchKey].title;
	}
	return title;
};
